import { Button, OutlinedSelect } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { TranslationsKeys } from '../../../../locales/translations';
import { useAcademicOffers } from '../loaders/AcademicOffersLoader';
import { useAcademicOfferFormData } from '../loaders/AcademicOfferFormDataLoader';
import { NonStructuredAcademicOfferStatus, NonStructuredType } from '../types';

interface SelectOption<T> {
  label: string;
  value: T;
}

interface FilterFormValues {
  processType: SelectOption<number> | null;
  programType: SelectOption<NonStructuredType> | null;
  academicLevel: SelectOption<number> | null;
  studyPlan: SelectOption<number> | null;
  modality: SelectOption<number> | null;
  campus: SelectOption<number> | null;
  school: SelectOption<number> | null;
  status: SelectOption<NonStructuredAcademicOfferStatus> | null;
}

const DEFAULTS: FilterFormValues = {
  processType: null,
  programType: null,
  academicLevel: null,
  studyPlan: null,
  modality: null,
  campus: null,
  school: null,
  status: null,
};

export function AcademicOffersSearch() {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );

  const { updateQuery, query } = useAcademicOffers();
  const { data: formData } = useAcademicOfferFormData();

  const { control, reset, handleSubmit } = useForm<FilterFormValues>({
    defaultValues: DEFAULTS,
  });

  const processTypeOptions = useMemo(
    () =>
      formData?.processTypes.map((pt) => ({ label: pt.name, value: pt.id })) ??
      [],
    [formData?.processTypes],
  );

  const programTypeOptions = useMemo(
    () => [
      {
        label: t('filters.programType.course'),
        value: NonStructuredType.COURSE,
      },
      {
        label: t('filters.programType.program'),
        value: NonStructuredType.PROGRAM_COURSE,
      },
    ],
    [t],
  );

  const academicLevelOptions = useMemo(
    () =>
      formData?.academicLevels.map((al) => ({
        label: al.name,
        value: al.id,
      })) ?? [],
    [formData?.academicLevels],
  );

  const studyPlanOptions = useMemo(
    () =>
      formData?.studyPlans.map((sp) => ({ label: sp.name, value: sp.id })) ??
      [],
    [formData?.studyPlans],
  );

  const modalityOptions = useMemo(
    () =>
      formData?.modalities.map((m) => ({ label: m.name, value: m.id })) ?? [],
    [formData?.modalities],
  );

  const campusOptions = useMemo(
    () => formData?.campuses.map((c) => ({ label: c.name, value: c.id })) ?? [],
    [formData?.campuses],
  );

  const schoolOptions = useMemo(
    () => formData?.schools.map((s) => ({ label: s.name, value: s.id })) ?? [],
    [formData?.schools],
  );

  const statusOptions = useMemo(
    () => [
      {
        label: t('filters.status.draft'),
        value: NonStructuredAcademicOfferStatus.Borrador,
      },
      {
        label: t('filters.status.active'),
        value: NonStructuredAcademicOfferStatus.Activa,
      },
      {
        label: t('filters.status.closed'),
        value: NonStructuredAcademicOfferStatus.Cerrada,
      },
    ],
    [t],
  );

  const handleSearch = useCallback(
    (values: FilterFormValues) => {
      updateQuery({
        page: 1,
        items_per_page: query.items_per_page,
        processTypeId: values.processType?.value ?? undefined,
        academicLevelId: values.academicLevel?.value ?? undefined,
        programType: values.programType?.value ?? undefined,
        studyPlanId: values.studyPlan?.value ?? undefined,
        modalityId: values.modality?.value ?? undefined,
        campusId: values.campus?.value ?? undefined,
        schoolId: values.school?.value ?? undefined,
        status: values.status?.value ?? undefined,
      });
    },
    [updateQuery, query],
  );

  const handleClean = useCallback(() => {
    reset(DEFAULTS);
    updateQuery({
      page: 1,
      items_per_page: query.items_per_page,
    });
  }, [reset, updateQuery, query]);

  return (
    <Row className="mb-4">
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={processTypeOptions}
          name="processType"
          label={t('filters.processType.label')}
          placeholder={t('filters.processType.placeholder')}
          isClearable
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={academicLevelOptions}
          name="academicLevel"
          label={t('filters.academicLevel.label', 'Nivel Académico')}
          placeholder={t('filters.academicLevel.placeholder', 'Todos')}
          isClearable
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={programTypeOptions}
          name="programType"
          label={t('filters.programType.label')}
          placeholder={t('filters.programType.placeholder')}
          isClearable
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={studyPlanOptions}
          name="studyPlan"
          label={t('filters.studyPlan.label')}
          placeholder={t('filters.studyPlan.placeholder')}
          isClearable
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={modalityOptions}
          name="modality"
          label={t('filters.modality.label')}
          placeholder={t('filters.modality.placeholder')}
          isClearable
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={schoolOptions}
          name="school"
          label={t('filters.school.label')}
          placeholder={t('filters.school.placeholder')}
          isClearable
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={campusOptions}
          name="campus"
          label={t('filters.campus.label')}
          placeholder={t('filters.campus.placeholder')}
          isClearable
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={statusOptions}
          name="status"
          label={t('filters.status.label')}
          placeholder={t('filters.status.placeholder')}
          isClearable
        />
      </Col>
      <Col xs={12} className="mt-3">
        <Row className="justify-content-end">
          <Col xs={6} sm={4} md={3} lg={3}>
            <Button
              text={t('filters.search')}
              size="md"
              onClick={handleSubmit(handleSearch)}
              fullwidth
            />
          </Col>
          <Col xs={6} sm={4} md={3} lg={3}>
            <Button
              text={t('filters.clean')}
              outlined
              size="md"
              onClick={handleClean}
              fullwidth
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
